import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import MobileNav from "@/components/MobileNav";
import RestTimer from "@/components/RestTimer";
import Onboarding from "@/components/Onboarding";
import PRCelebration from "@/components/PRCelebration";
import InstallPrompt from "@/components/InstallPrompt";
import Dashboard from "@/pages/Dashboard";
import WorkoutLogger from "@/pages/WorkoutLogger";
import WorkoutDetail from "@/pages/WorkoutDetail";
import Templates from "@/pages/Templates";
import TemplateDetail from "@/pages/TemplateDetail";
import ExerciseLibrary from "@/pages/ExerciseLibrary";
import ScheduleCalendar from "@/pages/ScheduleCalendar";
import Plans from "@/pages/Plans";
import WorkoutSession from "@/pages/WorkoutSession";
import MacroLookup from "@/pages/MacroLookup";
import AICoach from "@/pages/AICoach";
import BodyMetrics from "@/pages/BodyMetrics";
import Settings from "@/pages/Settings";
import PersonalRecords from "@/pages/PersonalRecords";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { WeightUnitProvider } from "@/lib/units";
import { getUserId, installAxiosInterceptor } from "@/lib/userId";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Install global X-User-Id interceptor on axios (for components that still use it)
installAxiosInterceptor(axios);

// Patch global fetch so every component (including third-party) auto-sends the
// user id without each call site having to remember.
if (typeof window !== "undefined" && !window.__uptopFetchPatched) {
  const origFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    // Only inject for our own backend
    try {
      const url = typeof input === "string" ? input : (input && input.url) || "";
      if (url.startsWith(API) || url.startsWith("/api")) {
        const headers = new Headers(init.headers || {});
        if (!headers.has("X-User-Id")) headers.set("X-User-Id", getUserId());
        return origFetch(input, { ...init, headers });
      }
    } catch (e) { /* fall through */ }
    return origFetch(input, init);
  };
  window.__uptopFetchPatched = true;
}

function App() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [workouts, setWorkouts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [newPRs, setNewPRs] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [quickLogTemplate, setQuickLogTemplate] = useState(null);
  const [activeSession, setActiveSession] = useState(null); // { template, scheduleEntryId }
  const [theme, setTheme] = useState(() => {
    try {
      const stored = localStorage.getItem("uptop-theme");
      const valid = ["dark", "light", "crimson", "cyan", "system"];
      return valid.includes(stored) ? stored : "dark";
    } catch (e) { return "dark"; }
  });
  const [resolvedTheme, setResolvedTheme] = useState("dark");

  // Apply theme to <html> + persist. For "system", follow OS preference and react to changes.
  useEffect(() => {
    try { localStorage.setItem("uptop-theme", theme); } catch (e) { /* ignore */ }

    const setData = (next) => {
      const root = document.documentElement;
      const current = root.getAttribute("data-theme");
      if (current === next) return;
      // Skip the fade on the very first paint
      if (current !== null) {
        root.classList.add("theme-transitioning");
        window.setTimeout(() => root.classList.remove("theme-transitioning"), 260);
      }
      root.setAttribute("data-theme", next);
      setResolvedTheme(next);
    };

    if (theme !== "system") {
      setData(theme);
      return;
    }

    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => setData(mql.matches ? "dark" : "light");
    apply();
    mql.addEventListener("change", apply);
    return () => mql.removeEventListener("change", apply);
  }, [theme]);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/profile`);
      setProfile(res.data);
    } catch (e) {
      setProfile(null);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [wRes, tRes, sRes, stRes] = await Promise.all([
        axios.get(`${API}/workouts`),
        axios.get(`${API}/templates`),
        axios.get(`${API}/schedule`),
        axios.get(`${API}/stats`),
      ]);
      setWorkouts(wRes.data);
      setTemplates(tRes.data);
      setSchedule(sRes.data);
      setStats(stRes.data);
    } catch (e) {
      console.error("Fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (profile === undefined) return;
    if (profile) {
      axios.post(`${API}/seed-templates`).catch(() => {});
      fetchData();
      // If the user just landed here after saving a shared template, show a
      // one-time toast pointing them to the Templates tab.
      try {
        const raw = localStorage.getItem("uptop-imported-template");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.name) {
            toast.success(`"${parsed.name}" added to your templates`, {
              description: "Find it under Workouts → Templates.",
              duration: 6000,
            });
          }
          localStorage.removeItem("uptop-imported-template");
        }
      } catch (e) { /* ignore */ }
    }
  }, [profile, fetchData]);

  const handleOnboardingComplete = async () => {
    await fetchProfile();
    axios.post(`${API}/seed-templates`).catch(() => {});
    fetchData();
  };

  const openWorkoutDetail = (workout) => {
    setSelectedWorkout(workout);
    setActiveTab("workout-detail");
  };

  const closeWorkoutDetail = () => {
    setSelectedWorkout(null);
    setActiveTab("logger");
  };

  const openTemplateDetail = (template) => {
    setSelectedTemplate(template);
    setActiveTab("template-detail");
  };

  const closeTemplateDetail = () => {
    setSelectedTemplate(null);
    setActiveTab("templates");
  };

  const handleQuickLog = (template) => {
    setQuickLogTemplate(template);
    setActiveTab("logger");
  };

  const handleStartSession = (template, scheduleEntryId) => {
    setActiveSession({ template, scheduleEntryId });
    setActiveTab("workout-session");
  };

  const finishSession = () => {
    setActiveSession(null);
    setActiveTab("dashboard");
    fetchData();
  };

  const cancelSession = () => {
    setActiveSession(null);
    setActiveTab("dashboard");
  };

  if (profile === undefined) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0F0F0F]">
        <div className="w-8 h-8 border-2 border-[#E05A1D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return <Onboarding api={API} onComplete={handleOnboardingComplete} />;
  }

  const renderPage = () => {
    switch (activeTab) {
      case "dashboard":
        return <Dashboard stats={stats} workouts={workouts} api={API} profile={profile} schedule={schedule} templates={templates} onQuickLog={handleQuickLog} onStartSession={handleStartSession} onRefresh={fetchData} onOpenWorkout={openWorkoutDetail} onNavigate={setActiveTab} />;
      case "logger":
        return <WorkoutLogger workouts={workouts} templates={templates} api={API} onRefresh={fetchData} onOpenWorkout={openWorkoutDetail} onNewPRs={setNewPRs} quickLogTemplate={quickLogTemplate} onQuickLogConsumed={() => setQuickLogTemplate(null)} />;
      case "workout-detail":
        return <WorkoutDetail workout={selectedWorkout} api={API} onBack={closeWorkoutDetail} />;
      case "templates":
        return <Templates templates={templates} api={API} onRefresh={fetchData} profile={profile} categoryFilter={categoryFilter} onOpenTemplate={openTemplateDetail} onQuickLog={handleQuickLog} onNavigate={setActiveTab} />;
      case "template-detail":
        return <TemplateDetail template={selectedTemplate} api={API} onBack={closeTemplateDetail} />;
      case "library":
        return <ExerciseLibrary api={API} templates={templates} onBack={() => setActiveTab("templates")} onTemplatesRefresh={fetchData} />;
      case "calendar":
        return <ScheduleCalendar schedule={schedule} templates={templates} api={API} onRefresh={fetchData} />;
      case "plans":
        return <Plans api={API} templates={templates} profile={profile} onScheduleRefresh={fetchData} onTemplatesRefresh={fetchData} />;
      case "workout-session":
        return activeSession ? (
          <WorkoutSession
            api={API}
            template={activeSession.template}
            scheduleEntryId={activeSession.scheduleEntryId}
            onFinish={finishSession}
            onCancel={cancelSession}
          />
        ) : null;
      case "macros":
        return <MacroLookup api={API} profile={profile} />;
      case "ai":
        return <AICoach api={API} />;
      case "body":
        return <BodyMetrics api={API} />;
      case "settings":
        return <Settings api={API} profile={profile} onProfileUpdate={fetchProfile} theme={theme} onThemeChange={setTheme} />;
      case "prs":
        return <PersonalRecords api={API} profile={profile} />;
      default:
        return <Dashboard stats={stats} workouts={workouts} api={API} profile={profile} schedule={schedule} templates={templates} onQuickLog={handleQuickLog} onStartSession={handleStartSession} onRefresh={fetchData} onOpenWorkout={openWorkoutDetail} onNavigate={setActiveTab} />;
    }
  };

  return (
    <WeightUnitProvider>
      <div className="flex h-screen bg-[#0F0F0F] overflow-hidden" data-testid="app-container">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} onCategoryChange={setCategoryFilter} />
      <MobileNav activeTab={activeTab} setActiveTab={setActiveTab} onCategoryChange={setCategoryFilter} />
      <main className="flex-1 overflow-y-auto p-4 pt-16 md:pt-8 md:p-8">
        {loading ? (
          <div className="flex items-center justify-center h-full" data-testid="loading-spinner">
            <div className="w-8 h-8 border-2 border-[#E05A1D] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          renderPage()
        )}
      </main>
      <RestTimer />
      {newPRs && <PRCelebration prs={newPRs} onClose={() => setNewPRs(null)} />}
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: resolvedTheme === "light"
            ? { background: '#FFFFFF', border: '1px solid #E4E4E7', color: '#18181B' }
            : { background: '#161616', border: '1px solid #27272A', color: '#fff' }
        }}
      />
      <InstallPrompt />
    </div>
    </WeightUnitProvider>
  );
}

export default App;
